const parseMaybeJson = (value, fallback = {}) => {
  if (!value) return fallback;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }
  return value;
};

const normalizeToken = (value) => String(value || '').trim().toLowerCase();

const includesDecoration = (value) => {
  const token = normalizeToken(value);
  return Boolean(token && /decoration|decorator|art/.test(token) && !/no[_\s-]*decoration/.test(token));
};

const isNoDecorationType = (value) => /no[_\s-]*decoration/i.test(String(value || ''));

const hasDecorationCharge = (charge) => {
  if (!charge || typeof charge !== 'object') return false;
  return Boolean(
    charge.decoratorId ||
    charge.DecoratorId ||
    charge.decorator_id ||
    charge.DecoratorDetails?.decoratorId ||
    charge.decorationId ||
    charge.DecorationId ||
    includesDecoration(charge.type) ||
    includesDecoration(charge.chargeType) ||
    includesDecoration(charge.category) ||
    includesDecoration(charge.title) ||
    includesDecoration(charge.Name) ||
    includesDecoration(charge.item)
  );
};

const flattenCharges = (charges) => {
  if (typeof charges === 'string') return flattenCharges(parseMaybeJson(charges, []));
  if (!Array.isArray(charges)) return [];
  return charges.flatMap((charge) => {
    const nested = [];
    if (Array.isArray(charge?.charges)) nested.push(...flattenCharges(charge.charges));
    if (Array.isArray(charge?.Charge)) nested.push(...flattenCharges(charge.Charge));
    return [charge, ...nested];
  });
};

const appliesToCurrentPoDecoration = (appliesTo, po) => {
  if (!appliesTo) return false;
  const targets = Array.isArray(appliesTo) ? appliesTo : [appliesTo];
  const supplierInfo = parseMaybeJson(po?.supplierInfo, {});
  const poIds = new Set([po?.poId, po?.id, po?.purchaseOrderId, po?.poNumber].filter(Boolean).map(String));
  const decoratorId = supplierInfo?.decoratorId ? String(supplierInfo.decoratorId) : '';
  const orderItemIds = new Set([
    ...(Array.isArray(po?.items) ? po.items.map((item) => item?.id || item?.ProductId || item?.OrderItemId) : []),
    ...(Array.isArray(supplierInfo?.approvedLines) ? supplierInfo.approvedLines.map((ref) => ref?.orderItemId || ref?.productId || ref?.line?.id) : []),
  ].filter(Boolean).map(String));

  return targets.some((target) => {
    if (typeof target === 'string' || typeof target === 'number') {
      const raw = String(target);
      return poIds.has(raw) || (decoratorId && raw === decoratorId) || orderItemIds.has(raw) || includesDecoration(raw);
    }
    if (!target || typeof target !== 'object') return false;
    const targetPo = target.poId || target.purchaseOrderId || target.purchaseOrderNumber || target.poNumber;
    const targetDecorator = target.decoratorId || target.DecoratorId || target.decorator_id;
    const targetOrderItem = target.orderItemId || target.OrderItemId || target.productId || target.ProductId;
    return Boolean(
      (targetPo && poIds.has(String(targetPo))) ||
      (targetDecorator && decoratorId && String(targetDecorator) === decoratorId) ||
      (targetOrderItem && orderItemIds.has(String(targetOrderItem))) ||
      includesDecoration(target.type) ||
      includesDecoration(target.scope) ||
      includesDecoration(target.relationship)
    );
  });
};

export const isDecorationResponsiblePurchaseOrder = (po = {}) => {
  const rawPo = po?._poRaw || po;
  const supplierInfo = parseMaybeJson(rawPo?.supplierInfo || po?.supplierInfo, {});
  const purchaseOrderType = rawPo?.purchaseOrderType || po?.purchaseOrderType;
  const supplierType = supplierInfo?.type || rawPo?.supplierType || po?.supplierType;

  if (isNoDecorationType(purchaseOrderType) || isNoDecorationType(supplierType)) return false;

  const linkedPurchaseOrders = Array.isArray(supplierInfo?.linkedPurchaseOrders) ? supplierInfo.linkedPurchaseOrders : [];
  const isGoodsPoLinkedToDecoration = linkedPurchaseOrders.some((linked) => (
    normalizeToken(linked?.relationship) === 'goods_to_decoration' &&
    !includesDecoration(purchaseOrderType) &&
    !includesDecoration(supplierType) &&
    !supplierInfo?.decoratorId
  ));
  if (isGoodsPoLinkedToDecoration) return false;

  if (includesDecoration(purchaseOrderType) || includesDecoration(supplierType)) return true;
  if (supplierInfo?.decoratorId) return true;
  if (flattenCharges(supplierInfo?.charges).some(hasDecorationCharge)) return true;

  const approvedLines = Array.isArray(supplierInfo?.approvedLines) ? supplierInfo.approvedLines : [];
  if (approvedLines.some((ref) => {
    const decos = ref?.line?.Decorations || ref?.line?.decorations || ref?.Decorations || ref?.decorations;
    return Array.isArray(decos) && decos.some((deco) => {
      const decoDecoratorId = deco?.DecoratorDetails?.decoratorId || deco?.decoratorId || deco?.DecoratorId;
      return Boolean(decoDecoratorId && supplierInfo?.decoratorId && String(decoDecoratorId) === String(supplierInfo.decoratorId));
    });
  })) return true;

  const proofs = Array.isArray(rawPo?.proofs) ? rawPo.proofs : [];
  if (proofs.some((proof) => appliesToCurrentPoDecoration(proof?.appliesTo, rawPo))) return true;

  return false;
};

export const seedArtworkAttachmentSelection = (artworkFiles = [], po = {}) => {
  const shouldPreselectArtwork = isDecorationResponsiblePurchaseOrder(po);
  return (Array.isArray(artworkFiles) ? artworkFiles : []).map((artwork) => ({
    ...artwork,
    active: shouldPreselectArtwork,
  }));
};
